import { FormattedMessage, useIntl } from "react-intl";
import { Disclosure } from "@/components/design/Disclosure";
import { NumberStepper } from "@/components/design/NumberStepper";
import { PlusButton } from "@/components/design/PlusButton";
import { Select } from "@/components/design/Select";
import {
  newCompanyCondition,
  type CompanyConditionByTypename,
  type CompanyMatchCondition,
  type CriterionRequirement,
} from "../design-codec";
import type { MatchData } from "../match-design-types";
import { useRequirementsReferenceData } from "../use-requirements-reference-data";
import {
  RequirementControls,
  SubsectionHeader,
} from "./row-primitives";
import { MatchSkillsDiagram } from "./MatchSkillsDiagram";

// The requirement editor of a company match (RANK_COMPANIES). It edits COMPANY criteria and never
// person ones: genesis keeps the two as separate sealed hierarchies that travel in separate design
// keys (design-codec CompanyMatchCondition), and a company run branches before the person path, so
// a person criterion authored here would be stored, saved and never looked at.
//
type IndustryRow = CompanyConditionByTypename<"CompanyIndustryCondition">;
type EmployeeCountRow = CompanyConditionByTypename<"CompanyEmployeeCountCondition">;
type ExpertiseRow = CompanyConditionByTypename<"CompanyExpertiseCondition">;

// A first criterion has to materialise the arm: null means "this design has no company arm", so the
// editor's first write is what turns it into a list (see MatchData.companyConditions).
const EMPTY_ARM: CompanyMatchCondition[] = [];

// Kevin's decision, and it is a decision rather than a default: the size criterion is a MINIMUM.
// The starting value is a round number a user recognises as a placeholder to change.
const DEFAULT_MINIMUM_EMPLOYEES = 50;

function mussFirst(
  a: { row: { requirement: CriterionRequirement } },
  b: { row: { requirement: CriterionRequirement } },
): number {
  return Number(b.row.requirement === "MUST") - Number(a.row.requirement === "MUST");
}

export function CompanyRequirementsPanel({
  data,
  update,
  readOnly = false,
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const { industries } = useRequirementsReferenceData();

  const rows = data.companyConditions ?? EMPTY_ARM;
  // Rows the codec could not type. Every kind genesis' sealed hierarchy holds today has a
  // subsection below, so this counts a kind added AFTER this build or a malformed row from a peer
  // client. They are round-tripped by the codec, so the panel only has to avoid pretending they are
  // not there — hence the count line at the bottom. Nothing here indexes into them, so an unknown
  // kind cannot reach a renderer and crash the editor.
  const unknownCount = (data.unknownCompanyConditions ?? []).length;

  const industryRows: Array<{ row: IndustryRow; index: number }> = [];
  const employeeRows: Array<{ row: EmployeeCountRow; index: number }> = [];
  const expertiseRows: Array<{ row: ExpertiseRow; index: number }> = [];
  rows.forEach((row, index) => {
    if (row.__typename === "CompanyIndustryCondition") industryRows.push({ row, index });
    else if (row.__typename === "CompanyEmployeeCountCondition") employeeRows.push({ row, index });
    else if (row.__typename === "CompanyExpertiseCondition") expertiseRows.push({ row, index });
  });

  // Index-based writes against the ONE list, so a criterion keeps its place across an edit and the
  // three subsections cannot fight over ordering the way three separate lists would.
  function patch(index: number, next: CompanyMatchCondition) {
    update({ companyConditions: rows.map((row, i) => (i === index ? next : row)) });
  }
  function remove(index: number) {
    update({ companyConditions: rows.filter((_, i) => i !== index) });
  }
  function add(condition: CompanyMatchCondition) {
    update({ companyConditions: [...rows, condition] });
  }

  // Muss first, Kann below (stable), same reading order as the person editors; the original index
  // rides along so patch/remove still address the real row after the display sort.
  industryRows.sort(mussFirst);
  employeeRows.sort(mussFirst);
  expertiseRows.sort(mussFirst);

  const industryPlaceholder = intl.formatMessage({
    id: "match.recruiting.branchePlaceholder",
    defaultMessage: "Branche wählen",
  });
  const removeLabel = intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" });
  const employeesUnit = intl.formatMessage({
    id: "match.company.employeesUnit",
    defaultMessage: "Mitarbeitende",
  });
  // The company page's own wording for naming an expertise, not a second copy of it: a criterion
  // and the company rows it is compared against must ask for the same kind of thing.
  const expertisePlaceholder = intl.formatMessage({
    id: "company.admin.competencies.labelPlaceholder",
    defaultMessage: "z. B. Regulatory Affairs",
  });

  return (
    <div className="space-y-3">
      <Disclosure
        title={intl.formatMessage({
          id: "match.companyReqsSection",
          defaultMessage: "Unternehmens-Anforderungen",
        })}
        defaultOpen
      >
        <div className="space-y-6">
          <div>
            <SubsectionHeader
              title={intl.formatMessage({
                id: "match.company.industryTitle",
                defaultMessage: "Branche",
              })}
            />
            <div className="space-y-2">
              {industryRows.map(({ row, index }) => (
                <div
                  key={`company-industry-${index}`}
                  data-testid="company-industry-row"
                  className="flex flex-nowrap items-center gap-3"
                >
                  <RequirementControls
                    requirement={row.requirement}
                    onChange={(requirement) => patch(index, { ...row, requirement })}
                    readOnly={readOnly}
                  />
                  <div className="min-w-0 flex-1">
                    <Select
                      value={row.industryId}
                      onChange={(industryId) => patch(index, { ...row, industryId })}
                      disabled={readOnly}
                      placeholder={industryPlaceholder}
                      options={industries}
                    />
                  </div>
                  {!readOnly ? (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      data-testid="company-industry-remove"
                      aria-label={removeLabel}
                      className="ml-auto shrink-0 rounded-md px-2 py-1 text-ink-muted hover:text-ink"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              ))}
              {!readOnly ? (
                <div className="pt-1" data-testid="company-industry-add">
                  <PlusButton
                    label={intl.formatMessage({
                      id: "match.recruiting.addBranche",
                      defaultMessage: "Branche",
                    })}
                    onClick={() =>
                      add(newCompanyCondition("CompanyIndustryCondition", { industryId: "", operator: "EQ" }))
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div>
            <SubsectionHeader
              title={intl.formatMessage({
                id: "match.company.employeesTitle",
                defaultMessage: "Unternehmensgröße",
              })}
            />
            <div className="space-y-2">
              {employeeRows.map(({ row, index }) => (
                <div
                  key={`company-employees-${index}`}
                  data-testid="company-employees-row"
                  className="flex flex-nowrap items-center gap-3"
                >
                  <RequirementControls
                    requirement={row.requirement}
                    onChange={(requirement) => patch(index, { ...row, requirement })}
                    readOnly={readOnly}
                  />
                  {/* "mindestens N Mitarbeitende" reads as one sentence, so the direction is stated
                      in words and there is no operator picker: the criterion has exactly one form
                      (a minimum), and a picker would offer a maximum the concept rules out. */}
                  <span className="shrink-0 text-smycles-label text-ink-muted">
                    <FormattedMessage id="match.company.atLeast" defaultMessage="mindestens" />
                  </span>
                  <NumberStepper
                    value={row.minimumEmployees}
                    onChange={(next) =>
                      patch(index, { ...row, minimumEmployees: Math.max(0, Math.round(next)) })
                    }
                    min={0}
                    step={10}
                    disabled={readOnly}
                    ariaLabel={employeesUnit}
                  />
                  <span className="shrink-0 text-smycles-label text-ink-muted">{employeesUnit}</span>
                  {!readOnly ? (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      data-testid="company-employees-remove"
                      aria-label={removeLabel}
                      className="ml-auto shrink-0 rounded-md px-2 py-1 text-ink-muted hover:text-ink"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              ))}
              {/* AT MOST ONE size criterion (Kevin, 2026-09-08: "max 1 is enough"). The affordance is
                  withdrawn rather than shown disabled: the one row it would author is on screen with
                  its own remove control, so a second button offers nothing and explains nothing.
                  Changing the bound is a swap — remove, then add.

                  Not only tidiness: the size criterion carries a fulfilment degree into the MUSS/KANN
                  sums that order the ranking, so two size rows would weight size twice against every
                  other criterion. */}
              {!readOnly && employeeRows.length === 0 ? (
                <div className="pt-1" data-testid="company-employees-add">
                  <PlusButton
                    label={intl.formatMessage({
                      id: "match.company.addEmployeeCount",
                      defaultMessage: "Größe",
                    })}
                    onClick={() =>
                      add(
                        newCompanyCondition("CompanyEmployeeCountCondition", {
                          minimumEmployees: DEFAULT_MINIMUM_EMPLOYEES,
                          operator: "GE",
                        }),
                      )
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div>
            <SubsectionHeader
              title={intl.formatMessage({
                id: "match.company.expertiseTitle",
                defaultMessage: "Fachwissen",
              })}
            />
            <MatchSkillsDiagram
              entries={expertiseRows.map(({ row, index }) => ({
                id: String(index),
                description: row.expertise,
                level: 0,
                requirement: row.requirement,
              }))}
              showLevels={false}
              readOnly={readOnly}
              placeholder={expertisePlaceholder}
              onToggleRequirement={(id, requirement) => {
                const index = Number(id);
                patch(index, { ...rows[index]!, requirement });
              }}
              onRename={(id, expertise) => {
                const index = Number(id);
                const row = rows[index];
                if (row?.__typename === "CompanyExpertiseCondition") {
                  patch(index, { ...row, expertise });
                }
              }}
              onDelete={(id) => remove(Number(id))}
              onAdd={(expertise, _level, requirement) =>
                add(newCompanyCondition("CompanyExpertiseCondition", {
                  expertise,
                  requirement,
                  operator: "GE",
                }))
              }
            />
          </div>
        </div>
      </Disclosure>

      {/* Neutral, textual, no border and no colour: a preserved criterion this build cannot render
          is a fact about the data, not a warning about it. Same sentence the person panel uses for
          the same situation. */}
      {unknownCount > 0 ? (
        <div className="text-smycles-label text-ink-muted">
          {intl.formatMessage(
            {
              id: "match.requirements.otherUnchanged",
              defaultMessage:
                "{count, plural, one {# weitere Anforderung bleibt} other {# weitere Anforderungen bleiben}} unverändert erhalten.",
            },
            { count: unknownCount },
          )}
        </div>
      ) : null}
    </div>
  );
}
